import { useTranslation } from "@/hooks/useTranslation"
import { Form } from "@/organisms/Form"
import { SpecificResources } from "@/organisms/Form/components/ResourcesSectionForm/components/SpecificResources"
import {
  getAgentsOffScopePlatforms,
  getAiPolicyScope,
  getScopeAgentOptions,
  getScopeOptions
} from "@/pages/GovernancePage/applications/ai-policies/useLiveAiPolicies.selector"
import { useAiPolicies } from "@/pages/GovernancePage/applications/policies/useAiPolicies"
import { AgentCoreScopeNote } from "@/pages/GovernancePage/components/organisms/AgentCoreScopeNote"

import type { Rule } from "@/atoms"
import type { LiveScopeResourcesProps } from "./LiveScopeResources.types"

const SCOPE_LIST = "specific_resources"

// The live policy's scope: with platforms chosen, the agent picker offers only
// the agents on them, and an agent off them is an error
export const LiveScopeResources = ({
  form,
  tForm
}: LiveScopeResourcesProps) => {
  const { t } = useTranslation()
  const { live } = useAiPolicies()
  const agents = live?.agentOptions ?? []
  const values = {
    resources: Form.useWatch("resources", form),
    specific_resources: Form.useWatch(SCOPE_LIST, form)
  }
  const options = getScopeOptions(
    t,
    getScopeAgentOptions(getAiPolicyScope(values), agents)
  )
  // A dependency on the list alone misses a change of one of its values
  const scopeFields = [
    "resources",
    ...(values.specific_resources ?? []).flatMap(
      (_: unknown, index: number) => [
        [SCOPE_LIST, index, "type"],
        [SCOPE_LIST, index, "value"]
      ]
    )
  ]
  const platformRules: Rule[] = [
    ({ getFieldValue }) => ({
      validator: async () => {
        const off = getAgentsOffScopePlatforms(
          getAiPolicyScope({
            resources: getFieldValue("resources"),
            specific_resources: getFieldValue(SCOPE_LIST)
          }),
          agents
        )

        if (off.length)
          throw new Error(
            t("governance.ai.live.form.resources.offPlatforms", {
              agents: off.map((agent) => agent.label).join(", ")
            })
          )
      }
    })
  ]

  return (
    <>
      <SpecificResources
        form={form}
        options={options}
        tForm={tForm}
        rules={[{ required: true, message: t("error.requiredField") }]}
      />
      <Form.Item
        name="scopePlatforms"
        dependencies={scopeFields}
        rules={platformRules}
      />
      <AgentCoreScopeNote />
    </>
  )
}
